import { useEffect, useState } from 'react';
import { CasesGeneralListActions } from './actions';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ISelect } from '../../../../../shared/types/shared';
import {
    CasesGeneral,
    CasesGeneralFilter,
} from '../../../../../shared/types/modules/cases-general';
import { TableTooltip } from '../../../../components/general/table';
import { useNavigate } from 'react-router-dom';
import { asyncGetCasesGenerals } from '../../../../../services/cases-general.service';
import DateTimeFormat from '../../../../components/dateTimeFormat';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import {
    isOnlyViewProfile,
    isSupervisor,
} from '../../../../../shared/types/profiles';

export const useCasesGeneralList = (filters?: CasesGeneralFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<CasesGeneral[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<CasesGeneralFilter>({});
    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<CasesGeneral>[] = [];

    if (!isSupervisor(type) && !isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <CasesGeneralListActions id={row.id} />,
        });
    }

    columns.push(
        {
            field: 'caseId',
            headerName: 'ID Caso',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.callInfo?.callId || ''} />
            ),
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.claims?.status as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'account',
            headerName: 'Cuenta',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip value={row.row.account?.document || ''} />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre / Razón social',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${row.row.account?.name} ${row.row.account?.lastName}`}
                />
            ),
        },
        {
            field: 'plate',
            headerName: 'Placa',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'numberAudatex',
            headerName: 'Número Audatex',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={`${row.row.createdBy?.name} ${row.row.createdBy?.surname}`}
                />
            ),
        },
        {
            field: 'compensation',
            headerName: 'Tipo de indemnización',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'typification',
            headerName: 'Tipificación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.typification as ISelect)?.code || ''}
                />
            ),
        },
        {
            field: 'channel',
            headerName: 'Canal',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.callInfo?.channel as ISelect)?.name || ''}
                />
            ),
        },
        {
            field: 'dateCreate',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTimeFormat dateAt={row.row.created_at} />,
        },
        {
            field: 'userCreate',
            headerName: 'Creado por',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => (
                <TableTooltip
                    value={`${row.row.account?.name} ${row.row.account?.lastName}`}
                />
            ),
        },
        {
            field: 'dateUpdate',
            headerName: 'Fecha última de modificación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTimeFormat dateAt={row.row.updated_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetCasesGenerals(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
